<template>
  <div class="modal-body-content-modal-background" @pointerdown="handleMouseDown" @pointerup="handleMouseUp">
    <div class="modal-body-content-modal">
      <div class="modal-body-edit-box" @click.stop="" ref="modal">
        <div class="modal-body-edit-box-title">
          <h2>{{ isAddCustomMarker ? $t('add_poi') : $t('modify_poi') }}</h2>
          <img src="@/assets/images/x_black.svg" @click.stop="isPOIModalSimpleOpen = false; isPOIModalSimpleOpen2 = false;"/>
        </div>
        <div class="modal-body-edit-box-content">
          <div class="modal-body-edit-box-content-main">
            <table>
              <tbody>
                <tr>
                  <th>POI {{ $t('name') }}</th>
                  <td><input v-model="markerName" id="markerName" :placeholder="$t('enter_your_poi_name')" required /></td>
                </tr>
                <tr>
                  <th>POI {{ $t('description') }}</th>
                  <td><input v-model="markerDescription" id="markerDescription" :placeholder="$t('enter_your_poi_description')" required /></td>
                </tr>
                <tr style="height: 100%;">
                  <th style="height: 100%;">{{ $t('image') }}</th>
                  <td style="height: fit-content;"><ImageUploadPreview style="height: 100px;"/></td>
                </tr>
                <tr>
                  <th>{{ $t('latitude') }}</th>
                  <td><input v-model="latitude" id="latitude" :placeholder="$t('enter_latitude')" required type="number" /></td>
                </tr>
                <tr>
                  <th>{{ $t('longitude') }}</th>
                  <td><input v-model="longitude" id="longitude" :placeholder="$t('enter_longitude')" required type="number" /></td>
                </tr>
              </tbody>
            </table>
            <div id="poiMap" style="height: 300px;">
            </div>
            <div class="button-group">
              <button type="button" @click.stop="isPOIModalSimpleOpen = false; isPOIModalSimpleOpen2 = false;" class="cancel-button">{{ $t('cancel') }}</button>
              <button v-if="selectedCustomMarker && !selectedCustomMarker.is_deleted" type="button" @click.stop="deletePOI(markerId)" class="delete-button">{{ $t('delete') }}</button>
              <button v-else-if="selectedCustomMarker && selectedCustomMarker.is_deleted" type="button" @click.stop="restorePOI(markerId)" class="retrieve-button">{{ $t('restore') }}</button>
              <button type="button" @click.stop="savePOI(markerName, markerDescription, latitude, longitude, isAddCustomMarker, markerId)" class="action-button">{{ $t('submit') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
  
<script setup>
// POI (Point Of Interest)
import { ref, onMounted, onUnmounted } from 'vue';
import L from 'leaflet';
import { imageUrl } from '../utils/ServerConnect';
import { loadingStore } from '@/utils/LoadingStore';
import ImageUploadPreview from '@/components/ImageUploadPreview.vue';
import { previewImage } from '@/utils/ImageUtility';
import { createCustomMarkerIcon, firstMapSetting, isAddCustomMarker, isPOIModalSimpleOpen, isPOIModalSimpleOpen2, newCustomMarkerLatLng, selectedCustomMarker } from '@/utils/MapSetting';
import { SendErrorLog } from '@/utils/GlobalUtility';
import { deletePOI, restorePOI, savePOI } from '@/utils/POIUtility';

const markerId = ref(0);
const markerName = ref('');
const markerDescription = ref('');
const latitude = ref();
const longitude = ref();
const poiMap = ref(null);
const poi = ref(null);

// API를 통해 장치정보 받아오기
const fetchData = async () => {
  loadingStore.showLoading();

  try {
    if (isAddCustomMarker.value) {
      latitude.value = newCustomMarkerLatLng.value.lat;
      longitude.value = newCustomMarkerLatLng.value.lng;
      previewImage.value = new URL('../assets/images/logo.png', import.meta.url).href;
    } else {
      if (!selectedCustomMarker.value) { isPOIModalSimpleOpen.value = false; isPOIModalSimpleOpen2.value = false; return; }
      const setMarkerImage = selectedCustomMarker.value.marker_image ? imageUrl() + selectedCustomMarker.value.marker_image : new URL('../assets/images/logo.png', import.meta.url).href;
      previewImage.value = setMarkerImage;

      markerId.value = selectedCustomMarker.value.marker_id;
      markerName.value = selectedCustomMarker.value.title;
      markerDescription.value = selectedCustomMarker.value.description;
      latitude.value = selectedCustomMarker.value.latitude;
      longitude.value = selectedCustomMarker.value.longitude;
    }
    // 여기에 지도에 마커 미리보기 추가하는 기능 추가해야함
    poi.value = L.marker([latitude.value, longitude.value], {icon: createCustomMarkerIcon(selectedCustomMarker.value?.marker_image), draggable: true})
    .on('dragend', (event) => {
      const { lat, lng } = event.target.getLatLng()
      latitude.value = lat;
      longitude.value = lng;
      poiMap.value.setView([latitude.value, longitude.value])  
    })
    .addTo(poiMap.value);
    poiMap.value.setView([latitude.value, longitude.value])
  } catch (error) {
    SendErrorLog('POI', error);
  }
  
  loadingStore.hideLoading();
};

// Initialize map on component mount
onMounted(async () => {
  poiMap.value = await firstMapSetting('poiMap', 17); // 지도 생성

  // 지도 클릭 이벤트
  poiMap.value.on('click', function (e) {
    if (poiMap.value) {
      latitude.value = e.latlng.lat;
      longitude.value = e.latlng.lng;
      poi.value.setLatLng([latitude.value, longitude.value])
      poiMap.value.setView([latitude.value, longitude.value])
    }
  });

  fetchData();
  loadingStore.hideLoading();
});
onUnmounted(() => {
  previewImage.value = null;
  isAddCustomMarker.value = false;
  newCustomMarkerLatLng.value = null;
  selectedCustomMarker.value = null;
})

const modal = ref(null);
let isDragging = false;
const handleMouseDown = (event) => {
  if (modal.value.contains(event.target)) {
    isDragging = true; // 모달 내부에서 클릭 시작
  }
};
const handleMouseUp = (event) => {
  if (!modal.value.contains(event.target) && !isDragging) {
    isPOIModalSimpleOpen.value = false; // 모달 외부에서 클릭 떼면 닫기
    isPOIModalSimpleOpen2.value = false;
  }
  isDragging = false; // 드래그 상태 초기화
};
</script>

<style>
:disabled {
  background-color: lightgray;
}
</style>